import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Timestamp, DocumentSnapshot } from '@angular/fire/firestore';
import { of, switchMap, tap } from 'rxjs';
import { Residencial } from '../models/residencial.model';
import { Usuario } from '../models/usuario.model';
import { ResidencialService } from './residencial.service';

const LS_KEY = 'rc_superadmin_state';

interface SuperadminState {
  superadminUser: Usuario;
}

type SerializedTimestamp = { seconds: number; nanoseconds: number; };
type SerializedUsuario = Omit<Usuario, 'creadoEn'> & {
  creadoEn: SerializedTimestamp;
};

/**
 * Estado global del superadmin durante su sesión.
 *
 * Responsabilidades:
 * - Mantener el perfil del superadmin en un signal reactivo.
 * - Exponer un listener en tiempo real de TODAS las residenciales de la plataforma.
 * - Persistir el perfil en localStorage para sobrevivir recargas de página sin
 *   volver a leer Firestore.
 *
 * Ciclo de vida del listener de residenciales:
 *   login  → loadForSuperadmin() → _superadminUser != null → _isActive = true
 *          → switchMap activa collectionData('residenciales') → listener abierto
 *   logout → clearState() → _superadminUser = null → _isActive = false
 *          → switchMap emite of([]) → listener cancelado automáticamente
 */
@Injectable({ providedIn: 'root' })
export class SuperadminStateService {
  private residencialSvc = inject(ResidencialService);

  private _superadminUser = signal<Usuario | null>(null);

  readonly superadminUser = this._superadminUser.asReadonly();

  // Driver booleano: activa o cancela el listener de residenciales según el estado de sesión.
  private readonly _isActive  = computed(() => this._superadminUser() !== null);
  private readonly _isActive$ = toObservable(this._isActive);

  // Pasa a true con la primera emisión de Firestore; permite distinguir "cargando" de "vacío".
  private _residencialesLoaded = signal(false);
  readonly residencialesLoaded = this._residencialesLoaded.asReadonly();

  // Un solo listener compartido para toda la sesión del superadmin.
  // Cualquier componente que lea este signal recibe actualizaciones en tiempo real
  // sin abrir su propio listener — Firestore solo notifica documentos que cambian.
  readonly residenciales = toSignal(
    this._isActive$.pipe(
      switchMap(active => active
        ? this.residencialSvc.getAll().pipe(tap(() => this._residencialesLoaded.set(true)))
        : of([]).pipe(tap(() => this._residencialesLoaded.set(false)))
      )
    ),
    { initialValue: [] as Residencial[] }
  );

  constructor() {
    // Rehidratar desde localStorage permite que el listener arranque de inmediato
    // en recargas de página, sin esperar un nuevo login.
    this.hydrateFromStorage();
  }

  /**
   * Carga el perfil del superadmin desde Firestore y activa el listener de residenciales.
   * Debe llamarse en AuthService.login() antes de navegar al módulo /superadmin.
   */
  async loadForSuperadmin(uid: string, userSnap: DocumentSnapshot): Promise<void> {
    if (!userSnap.exists()) throw new Error('Superadmin no encontrado en Firestore');

    const superadminUser = { id: userSnap.id, ...userSnap.data() } as Usuario;
    this._superadminUser.set(superadminUser);
    this.saveToStorage({ superadminUser });
  }

  /**
   * Limpia estado en memoria y localStorage.
   * El switchMap detecta _isActive = false y cancela el listener de Firestore.
   * Debe llamarse en AuthService.logout() antes de signOut().
   */
  clearState(): void {
    this._superadminUser.set(null);
    localStorage.removeItem(LS_KEY);
  }

  // ── Persistencia ────────────────────────────────────────────────────────────

  // Lee el estado guardado al iniciar la app. Si el JSON está corrupto, limpia
  // para evitar errores en cadena al intentar deserializar datos inválidos.
  private hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as { superadminUser: SerializedUsuario };
      this._superadminUser.set(this.deserializeUsuario(parsed.superadminUser));
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }

  private saveToStorage(state: SuperadminState): void {
    const serialized = { superadminUser: this.serializeUsuario(state.superadminUser) };
    localStorage.setItem(LS_KEY, JSON.stringify(serialized));
  }

  // ── Serialización de Timestamps ─────────────────────────────────────────────
  // Firestore Timestamp no es JSON-serializable directamente.
  // Se guarda como { seconds, nanoseconds } y se reconstruye al leer.

  private serializeUsuario(u: Usuario): SerializedUsuario {
    return {
      ...u,
      creadoEn: { seconds: u.creadoEn.seconds, nanoseconds: u.creadoEn.nanoseconds }
    };
  }

  private deserializeUsuario(raw: SerializedUsuario): Usuario {
    return {
      ...raw,
      creadoEn: new Timestamp(raw.creadoEn.seconds, raw.creadoEn.nanoseconds)
    };
  }
}
